import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { API } from "@service/system/api.service";
import { Observable, throwError } from "rxjs";
import { catchError, retry } from "rxjs/operators";
import { HttpHeaders } from "@angular/common/http";

/**
 * An http manager service that helps facilitate http calls.
 *
 * @author    John Ying <john.ying@childrenshospitals.org>
 * @created   9/22/2022
 */
@Injectable()
export class HttpManager {
    constructor(private httpClient: HttpClient) { }

    /**
     * Makes an http DELETE call to the HUB database and returns an Observable of the given type.
     *
     * @param url    - the full URL address
     *
     * @returns the http DELETE response
     */
    httpDelete<T>(url: string, params?: Record<string, unknown>): Observable<T> {
        const httpOptions = Object.assign({}, API.defaultHTTPOptions);
        httpOptions["params"] = params;
        return this.httpClient.delete<T>(`${url}`, httpOptions).pipe(catchError(this.handleError));
    }

    /**
     * Makes an http GET call to the HUB database and returns an Observable of the given type.
     *
     * @param url       - the full URL address
     * @param params    - the key-value query parameters
     *
     * @returns the http GET response
     */
    httpGet<T>(url: string, params?: Record<string, unknown>): Observable<T> {
        const httpOptions = Object.assign({}, API.defaultHTTPOptions);
        httpOptions["params"] = params;
        return this.httpClient.get<T>(`${url}`, httpOptions)
            .pipe(retry(1), catchError(this.handleError));
    }

    /**
     * Makes an http POST call to the HUB database and returns an Observable of the given type.
     *
     * @param url       - the full URL address
     * @param body      - the POST body
     * @param params    - the key-value query parameters
     *
     * @returns the http POST response
     */
    httpPost<T>(url: string, body: any | null, params?: Record<string, unknown>): Observable<T> {
        const httpOptions = Object.assign({}, API.defaultHTTPOptions);
        httpOptions["params"] = params;
        const postBody = body === null || body === undefined ? null : JSON.stringify(body);
        return this.httpClient
            .post<T>(url, postBody, httpOptions);
    }

	/**
     * Makes an http POST call to the HUB database with media options and returns an Observable of the given type.
     *
     * @param url       - the full URL address
     * @param body      - the POST body
     * @param params    - the key-value query parameters
     *
     * @returns the http POST response
     */
    httpPostMediaFile<T>(url: string, body: any | null, params?: Record<string, unknown>): Observable<T> {
        const httpOptions = Object.assign({}, API.mediaHTTPOptions);
        httpOptions["params"] = params;
        const postBody = body === null || body === undefined ? null : body;
        return this.httpClient
            .post<T>(url, postBody, httpOptions);
    }

    /**
     * Makes an http POST call to the HUB database and returns an Observable of the given type
     * with a blob response.
     *
     * @param url       - the full URL address
     * @param body      - the POST body
     * @param params    - the key-value query parameters
     *
     * @returns the http POST blob response
     */
    httpPostBlob<T>(url: string, body: any | null, params?: Record<string, unknown>): Observable<T> {
        const blobHttpOptions = {
            headers: new HttpHeaders({
            "Content-Type": "application/json; charset=utf-8"
            })
        };
        blobHttpOptions["params"] = params;
        blobHttpOptions["responseType"] = 'blob';
        const postBody = body === null || body === undefined ? null : JSON.stringify(body);
        return this.httpClient
            .post<T>(url, postBody, blobHttpOptions);
    }

    /**
     * Makes an http PUT call to the HUB database and returns an Observable of the given type.
     *
     * @param url       - the full URL address
     * @param body      - the POST body
     *
     * @returns the http PUT response
     */
    httpPut<T>(url: string, body: any | null, params?: Record<string, unknown>): Observable<T> {
        const httpOptions = Object.assign({}, API.defaultHTTPOptions);
        httpOptions["params"] = params;
        return this.httpClient.put<T>(`${url}`, body, httpOptions)
            .pipe(retry(1), catchError(this.handleError));
    }

    /**
     * Makes an http PATCH call to the HUB database and returns an Observable of the given type.
     *
     * @param url       - the full URL address
     * @param body      - the PATCH body
     *
     * @returns the http PATCH response
     */
    httpPatch<T>(url: string, body: any | null): Observable<T> {
        return this.httpClient.patch<T>(url, body);
    }

    private handleError(error: HttpErrorResponse): Observable<never> {
        let errorMessage = "";
        if (error.error instanceof ErrorEvent) {
            // client-side error
            errorMessage = `Client Error: ${error.error.message}`;
        } else {
            // server-side error
            errorMessage = `${error.url}\nError Code: ${error.status}\nMessage: ${error.error}`;
        }
		console.error(errorMessage);
		return throwError(error);
    }
}
